// The page-load fetch for a ConversationPage — V6 §A.8.
//
// ONE Promise.all over the load-time RPCs, each member wrapped in its own .catch
// so a single failure never blocks the batch (the CdpDashboardPage idiom the spec
// names). The list, the drill and the detail fetch SEPARATELY — they are not here.
//
// THE HEADLINE GUARANTEE OF THIS BUILD (V6 changelog, and the one the reviewers
// trace): every modality-scoped RPC is called with the PAGE's modality, so the
// Phone page returns 0 chat rows and the Chat page returns 0 voice rows. Before the
// backend fix a missing p_modality mixed the two channels — a Phone panel showed 55
// after-hours calls when Phone alone was 31. The fix is only real if the page
// threads modality to ALL of them; passing it to some and defaulting others is the
// exact defect. So this function passes `modality` explicitly to summary, bookings,
// funnel, trend, followups, knowledge_gaps AND agent_value (now modality-scoped as
// of V6 §A.5.3), and the unit test asserts none of them is left to a default.
//
// The two single-channel RPCs are gated on modality rather than modality-scoped:
//   - call-loss histogram (greeting loss) is Phone-only — there is no greeting to
//     abandon on chat — so it is fetched for voice and skipped for chat.
//   - conversation topics is Chat-only (E.13: not rendered on Phone), and its
//     wrapper pins p_modality:'chat' internally, so it is fetched only for chat.
// Sentences are range-keyed, not modality-scoped, and never throw.

import {
  getConversationSummary,
  getConversationBookings,
  getInquiryFunnel,
  getConversationTrend,
  getConversationFollowups,
  getKnowledgeGaps,
  getAgentValue,
  getCallLossHistogram,
  getConversationTopics,
  conversationSentences,
} from "../../api/conversation";

// A load-time cap that matches each panel's E-entry "maximum": five follow-ups and
// five knowledge gaps render first, with "See all" re-fetching at 50. Kept here so
// the page-load call is the small one and the expand is a separate fetch.
const LOAD_FOLLOWUP_LIMIT = 5;
const LOAD_GAP_LIMIT = 5;
const FOLLOWUP_WINDOW_DAYS = 30;

/**
 * Fetch every load-time panel payload for one page.
 *
 * @param {string} clientId
 * @param {{ start: string, end: string, modality: 'voice'|'chat', hourlyRate?: number|null }} opts
 * @returns {Promise<Object>} a map of panel payloads; a failed member is null, never
 *   a rejected batch. Keys: funnel, agentValue, bookings, summary, followups, gaps,
 *   trend, callLoss (voice only, else null), topics (chat only, else null),
 *   sentences ({ sentences, served, exact }), and `failed` — the keys whose request
 *   threw (e.g. a statement timeout on a long range), so the page can tell a failed
 *   load from a genuinely empty one.
 */
export const loadConversationData = async (clientId, { start, end, modality, hourlyRate = null } = {}) => {
  const isVoice = modality === "voice";
  const isChat = modality === "chat";
  const failed = [];
  const guard = (key, p) => p.catch(() => { failed.push(key); return null; });

  const [
    funnel,
    agentValue,
    bookings,
    summary,
    followups,
    gaps,
    trend,
    callLoss,
    topics,
    sentences,
  ] = await Promise.all([
    guard("funnel", getInquiryFunnel(clientId, start, end, modality)),
    guard("agentValue", getAgentValue(clientId, start, end, hourlyRate, modality)),
    guard("bookings", getConversationBookings(clientId, start, end, modality)),
    guard("summary", getConversationSummary(clientId, start, end, modality)),
    guard("followups", getConversationFollowups(clientId, { days: FOLLOWUP_WINDOW_DAYS, limit: LOAD_FOLLOWUP_LIMIT, modality })),
    guard("gaps", getKnowledgeGaps(clientId, { limit: LOAD_GAP_LIMIT, modality })),
    guard("trend", getConversationTrend(clientId, start, end, modality)),
    // Greeting loss is Phone-only; the chat page never asks for it.
    isVoice ? guard("callLoss", getCallLossHistogram(clientId, start, end)) : Promise.resolve(null),
    // Topics is Chat-only (E.13). Its wrapper pins p_modality:'chat' internally.
    isChat ? guard("topics", getConversationTopics(clientId, start, end)) : Promise.resolve(null),
    // Sentences never throw; no extra guard needed.
    conversationSentences(clientId, start, end),
  ]);

  return { funnel, agentValue, bookings, summary, followups, gaps, trend, callLoss, topics, sentences, failed };
};

export default loadConversationData;
